import React, { useEffect, useRef } from "react";
import { useWidgetPolls } from "@/hooks/polls/useWidgetPolls";
import { Link, useParams } from "react-router-dom";
import { useUserVotes } from "@/hooks/polls/useUserVotes";
import useCategories from "@/hooks/useCategories";
import { usePollFilters } from "@/hooks/polls/usePollFilters";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronUp, ChevronDown } from "lucide-react";
import CategoryFilters from "./CategoryFilters";
import ActivePollsSection from "./ActivePollsSection";
import PollsHeader from "./PollsHeader";
import PollsLoadingState from "./PollsLoadingState";
import PollsErrorState from "./PollsErrorState";
import PollsEmptyState from "./PollsEmptyState";
import ClosedPollsGrid from "./ClosedPollsGrid";
import { useCountry } from "@/contexts/country";
import { usePollVotesRealtime } from "@/hooks/polls/usePollVotesRealtime";
import { useUniversalPollVoting } from "@/hooks/polls/useUniversalPollVoting";
import { useCentralizedVoting } from "@/hooks/voting/useCentralizedVoting";

interface CountryPollsTabProps {
  countryName?: string;
}

const CountryPollsTab: React.FC<CountryPollsTabProps> = ({ countryName }) => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const refreshTriggered = useRef(false);
  const lastCountryCode = useRef<string | undefined>(countryCode);

  const { userVotes, setUserVotes } = useUserVotes();
  const { categories } = useCategories();

  // Use centralized voting for permission checks
  const { validateVote } = useCentralizedVoting();

  const {
    polls: allPolls,
    setPolls,
    currentPollIndex,
    isLoading,
    fetchError,
    nextPoll,
    prevPoll,
    refresh,
    closedPolls,
    userVotes: pollUserVotes,
  } = useWidgetPolls(countryCode);

  // Set up realtime subscription for poll votes
  const { markRecentVote } = usePollVotesRealtime(
    allPolls,
    setPolls,
    userVotes,
    setUserVotes
  );

  // Use the universal voting hook
  const { handleVote, canVote: universalCanVote } = useUniversalPollVoting({
    polls: allPolls,
    setPolls,
    userVotes,
    setUserVotes,
    markRecentVote,
    onRefresh: refresh,
  });

  const {
    selectedCategory,
    setSelectedCategory,
    showClosedPolls,
    setShowClosedPolls,
    filteredPolls,
    filteredClosedPolls,
  } = usePollFilters(allPolls, closedPolls);

  useEffect(() => {
    if (!refreshTriggered.current || lastCountryCode.current !== countryCode) {
      lastCountryCode.current = countryCode;
      refreshTriggered.current = true;

      setTimeout(() => {
        refresh();
      }, 100);
    }
  }, [countryCode, refresh]);

  if (isLoading) {
    return <PollsLoadingState countryName={countryName} />;
  }

  if (fetchError) {
    return <PollsErrorState countryName={countryName} onRefresh={refresh} />;
  }

  if (!allPolls || allPolls.length === 0) {
    return <PollsEmptyState countryName={countryName} onRefresh={refresh} />;
  }

  const currentPoll = filteredPolls[currentPollIndex];
  const hasVoted = userVotes[currentPoll?.id] !== undefined;

  // Use centralized voting validation for permission checks
  const checkCanVote = async (pollId: string): Promise<boolean> => {
    try {
      return await validateVote(pollId);
    } catch (error) {
      console.error("Error validating vote permission:", error);
      return false;
    }
  };

  // Create a wrapper vote handler that uses the poll ID
  const handlePollVote = async (optionId: number) => {
    if (currentPoll) {
      await handleVote(currentPoll.id, optionId);
    }
  };

  // Generate appropriate cannot vote reason based on poll settings
  const getCannotVoteReason = (poll: any): string => {
    if (!poll) return "Poll not available";

    // If poll allows anonymous voting, public users can vote
    if (poll.anonymous_allowed) {
      return "";
    }

    // If poll allows public voting, authenticated users can vote
    if (poll.public_voting_allowed) {
      return "Please sign in to vote on this poll";
    }

    // Otherwise, only verified pollsters can vote
    return "This poll is only available to verified pollsters";
  };

  return (
    <div className="bg-background/95 backdrop-blur-sm p-4 md:p-6 lg:p-8 rounded-xl shadow-lg border border-border/50 w-full max-w-7xl mx-auto">
      {/* Back to polls link */}
      <div className="mb-6">
        <Link
          to="/polls"
          className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
        ></Link>
      </div>

      <PollsHeader
        countryName={countryName}
        currentIndex={currentPollIndex}
        totalPolls={filteredPolls.length}
        onRefresh={refresh}
      />

      <CategoryFilters
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="space-y-10">
        <section className="bg-card/50 p-6 rounded-xl border border-border/30 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-1 w-8 bg-primary rounded-full"></div>
            <h3 className="text-xl font-bold text-foreground">
              Active Polls
            </h3>
            <div className="h-1 flex-1 bg-border/30 rounded-full"></div>
          </div>
          {currentPoll && (
            <ActivePollsSection
              filteredPolls={filteredPolls}
              currentPoll={currentPoll}
              currentPollIndex={currentPollIndex}
              hasVoted={hasVoted}
              canVote={universalCanVote(currentPoll.id)}
              handleVote={handlePollVote}
              nextPoll={nextPoll}
              prevPoll={prevPoll}
              refresh={refresh}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              userVotes={userVotes}
              cannotVoteReason={getCannotVoteReason(currentPoll)}
            />
          )}
        </section>

        {filteredClosedPolls.length > 0 && (
          <section className="bg-card/30 p-6 rounded-xl border border-border/20">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="h-1 w-6 bg-muted-foreground/40 rounded-full"></div>
                <h3 className="text-xl font-bold text-foreground">
                  Closed Polls
                </h3>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowClosedPolls(!showClosedPolls)}
                className="text-sm font-medium border-border/50 hover:bg-secondary/80"
              >
                {showClosedPolls ? (
                  <>
                    Hide <ChevronUp className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            {showClosedPolls && (
              <div className="w-full overflow-x-auto">
                <ClosedPollsGrid
                  polls={filteredClosedPolls}
                  userVotes={userVotes}
                />
              </div>
            )}
          </section>
        )}

        {selectedCategory &&
          filteredPolls.length === 0 &&
          filteredClosedPolls.length === 0 && (
            <div className="py-8 text-center bg-card/20 rounded-xl border border-border/20">
              <p className="text-muted-foreground mb-4 text-base">
                No polls available in this category.
              </p>
              <Button
                onClick={() => setSelectedCategory(null)}
                variant="outline"
                size="default"
                className="font-medium"
              >
                View All Categories
              </Button>
            </div>
          )}
      </div>

      <div className="mt-8 pt-6 border-t border-border/30">
        <p className="text-muted-foreground text-sm leading-relaxed">
          These polls are conducted with our network of verified pollsters.
          Results are updated in real-time.
        </p>
      </div>
    </div>
  );
};

export default CountryPollsTab;
